const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * Calculate the next installment date based on the given date.
 *
 * @param {Date} date - The current date.
 * @returns {Date} The next installment date.
 */
export function calculateNextInstallmentDate(date: Date): Date {
    const day = date.getDate();
    const year = date.getFullYear();
    const month = date.getMonth();
    const endOfMonth = (year: number, month: number): Date => {
        return new Date(year, month + 1, 0);
    };

    return day >= 15
        ? new Date(endOfMonth(year, month + 1).getTime() + MILLISECONDS_PER_DAY)
        : new Date(endOfMonth(year, month).getTime() + MILLISECONDS_PER_DAY);
}

/**
 * Calculate the number of days between two dates.
 *
 * @param {Date} start - The start date.
 * @param {Date} end - The end date.
 * @returns {number} The number of days between the start and end dates.
 */
function calculateDaysBetween(start: Date, end: Date): number {
    return (
        Math.ceil((end.getTime() - start.getTime()) / MILLISECONDS_PER_DAY) + 1
    );
}

/**
 * Calculate the monthly payment for a loan.
 *
 * @param {number} loanAmount - The total loan installmentAmount.
 * @param {number} annualInterestRate - The annual interest rate (as a decimal).
 * @param {number} months - The number of months over which the loan will be repaid.
 * @returns {number} The monthly payment installmentAmount.
 */
function calculateMonthlyPayment(
    loanAmount: number,
    annualInterestRate: number,
    months: number
): number {
    const monthlyRate = annualInterestRate / 12;
    const rateFactor = Math.pow(1 + monthlyRate, months);
    return (loanAmount * monthlyRate * rateFactor) / (rateFactor - 1);
}

/**
 * Calculate the total interest paid over the life of the loan.
 *
 * @param {number} loanAmount - The total loan installmentAmount.
 * @param {number} annualInterestRate - The annual interest rate (as a decimal).
 * @param {number} months - The number of months over which the loan will be repaid.
 * @returns {number} The total interest paid.
 */
function calculateTotalInterest(
    loanAmount: number,
    annualInterestRate: number,
    months: number
): number {
    const monthlyPayment = calculateMonthlyPayment(
        loanAmount,
        annualInterestRate,
        months
    );
    const totalRepayment = monthlyPayment * months;
    return totalRepayment - loanAmount;
}

/**
 * Calculate the flat rate for a loan.
 *
 * @param {number} loanAmount - The total loan installmentAmount.
 * @param {number} totalInterest - The total interest paid over the life of the loan.
 * @param {number} tenorInMonths - The loan tenor in months.
 * @returns {number} The flat rate for the loan.
 */
function calculateFlatRate(
    loanAmount: number,
    totalInterest: number,
    tenorInMonths: number
): number {
    const tenorInYears = tenorInMonths / 12;
    return totalInterest / (loanAmount * tenorInYears);
}

/**
 * Calculate the monthly installment for a loan, including the next installment date.
 *
 * @param {Date} startDate - The loan start date.
 * @param {number} annualInterestRate - The annual interest rate (as a decimal).
 * @param {number} price - The total price of the item being financed.
 * @param {number} downPaymentPercentage - The percentage of the price to be paid upfront as a down payment.
 * @param {number} tenorInMonths - The loan tenor in months.
 * @returns {{ installmentAmount: number; firstInstallmentDate: Date }} The monthly installment and the next installment date.
 */
export function calculateMonthlyInstallment(
    startDate: Date,
    annualInterestRate: number,
    price: number,
    downPaymentPercentage: number,
    tenorInMonths: number
): { installmentAmount: number; firstInstallmentDate: Date } {
    const downPayment = price * downPaymentPercentage;
    const loanAmount = price - downPayment;

    const totalInterest = calculateTotalInterest(
        loanAmount,
        annualInterestRate,
        tenorInMonths
    );
    const flatRate = parseFloat(
        calculateFlatRate(loanAmount, totalInterest, tenorInMonths).toFixed(4)
    );
    const dailyFlatRate = flatRate / 360;

    const firstInstallmentDate = calculateNextInstallmentDate(startDate);

    const finalPaymentDate = new Date(firstInstallmentDate);

    finalPaymentDate.setMonth(finalPaymentDate.getMonth() + tenorInMonths - 1);

    const totalDays = calculateDaysBetween(startDate, finalPaymentDate);

    const totalInterestWithFlatRate = loanAmount * dailyFlatRate * totalDays;

    const totalRepayment = loanAmount + totalInterestWithFlatRate;

    const monthlyInstallment = totalRepayment / tenorInMonths;

    return {
        installmentAmount: Math.ceil(parseFloat(monthlyInstallment.toFixed(2))),
        firstInstallmentDate,
    };
}

/**
 * Calculate the maximum loan that a user can take based on the maximum monthly installment,
 * tenor, down payment, and start date, using the flat rate calculation method.
 *
 * @param {number} maxMonthlyInstallment - The maximum monthly installment the user can afford.
 * @param {number} annualInterestRate - The annual interest rate (as a decimal).
 * @param {Date} startDate - The loan start date.
 * @param {number} [tenorInMonths=72] - The loan tenor in months (default is 72).
 * @returns {number} The maximum loan the user can afford.
 */
export function calculateMaxLoan(
    maxMonthlyInstallment: number,
    annualInterestRate: number,
    startDate: Date,
    // eslint-disable-next-line @typescript-eslint/no-inferrable-types
    tenorInMonths: number = 72
): number {
    const nextInstallmentDate = calculateNextInstallmentDate(startDate);

    const finalPaymentDate = new Date(nextInstallmentDate);

    finalPaymentDate.setMonth(finalPaymentDate.getMonth() + tenorInMonths - 1);

    const totalDays = calculateDaysBetween(startDate, finalPaymentDate);
    const totalInterest = calculateTotalInterest(
        5000000,
        annualInterestRate,
        tenorInMonths
    );
    const flatRate = parseFloat(
        calculateFlatRate(5000000, totalInterest, tenorInMonths).toFixed(4)
    );
    const dailyFlatRate = flatRate / 360;

    return (
        (maxMonthlyInstallment * tenorInMonths) /
        (1 + dailyFlatRate * totalDays)
    );
}

/**
 * Generate loan plans based on the maximum EMI.
 *
 * @param {number} maxEMI - The maximum EMI the user is willing to pay. Default is `Infinity`, meaning no restriction on EMI.
 * @param {number} price - The total price of the item being financed.
 * @param {number} downPaymentPercentage - The percentage of the price to be paid upfront as a down payment (e.g., 20 for 20%).
 * @param {number} annualInterestRate - The annual interest rate (as a decimal, e.g., 0.05 for 5%).
 * @param {Date} startDate - The loan start date.
 * @returns {{
 *   plans: Record<number, { installmentAmount: number; firstInstallmentDate: Date; duration: number; administrative:number }>,
 *   minimumDuration: number | null,
 *   maximumDuration: number | null
 * }} An object containing:
 *   - `plans`: A record where the key is the loan duration (in months) and the value is an object containing:
 *       - `installmentAmount`: The monthly installment installmentAmount.
 *       - `firstInstallmentDate`: The date of the first installment.
 *       - `duration`: The loan duration in months.
 *       - `administrativeFees`: The administrative fees
 *   - `minimumDuration`: The shortest loan duration that fits the `maxEMI`, or `null` if no plan is valid.
 *   - `maximumDuration`: The longest loan duration that fits the `maxEMI`, or `null` if no plan is valid.
 *
 * @example
 * const plans = generateLoanPlans(5000, 100000, 0.2, 0.35, new Date('2024-01-01'));
 */
export function generateLoanPlans(
    maxEMI = Infinity,
    price: number,
    downPaymentPercentage: number,
    annualInterestRate: number,
    startDate: Date
): {
    plans: Record<
        number,
        {
            installmentAmountWithoutInsurance: number;
            insurance: number;
            installmentAmount: number;
            firstInstallmentDate: Date;
            duration: number;
            administrativeFees: number;
        }
    >;
    minimumDuration: number | null;
    maximumDuration: number | null;
} {
    const plans: Record<
        number,
        {
            installmentAmountWithoutInsurance: number;
            insurance: number;
            installmentAmount: number;
            firstInstallmentDate: Date;
            duration: number;
            administrativeFees: number;
        }
    > = {};
    let minimumDuration = Infinity;
    let maximumDuration: number | null = null;
    const administrativeFees = Math.floor(
        calculateAdministrativeFees(price, downPaymentPercentage, 0.025)
    );
    const monthlyInsurance = Math.ceil(calculateYearlyInsurance(price) / 12);

    for (let duration = 6; duration <= 72; duration += 6) {
        const { installmentAmount, firstInstallmentDate } =
            calculateMonthlyInstallment(
                startDate,
                annualInterestRate,
                price,
                downPaymentPercentage,
                duration
            );

        const installmentAmountWithInsurance =
            installmentAmount + monthlyInsurance;

        if (installmentAmountWithInsurance <= maxEMI) {
            plans[duration] = {
                installmentAmountWithoutInsurance: installmentAmount,
                installmentAmount: installmentAmountWithInsurance,
                firstInstallmentDate,
                duration,
                administrativeFees,
                insurance: monthlyInsurance,
            };

            if (duration < minimumDuration) {
                minimumDuration = duration;
            }
            maximumDuration = duration;
        }
    }
    return {
        plans,
        minimumDuration: minimumDuration === Infinity ? null : minimumDuration,
        maximumDuration,
    };
}

/**
 * Calculate administrative fees based on the price, down payment percentage, and fee rate.
 *
 * @param {number} price - The total price of the item being financed.
 * @param {number} downPaymentPercentage - The percentage of the price to be paid upfront as a down payment.
 * @param {number} feeRate - The administrative fee rate (e.g., 0.025 for 2.5%).
 * @returns {number} The administrative fees.
 */
export function calculateAdministrativeFees(
    price: number,
    downPaymentPercentage: number,
    feeRate: number
): number {
    return feeRate * (price * (1 - downPaymentPercentage));
}

/**
 * Calculate the insurance premium based on the car price.
 *
 * @param {number} carPrice - The price of the car.
 * @returns {number} The insurance premium.
 */
export function calculateYearlyInsurance(carPrice: number): number {
    if (carPrice <= 500_000) {
        return carPrice * 0.023;
    } else if (carPrice > 500_000 && carPrice <= 1_000_000) {
        return carPrice * 0.02;
    } else {
        return carPrice * 0.019;
    }
}
